import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useState } from "react";

import {
  BarChart,
  Button,
  Card,
  CardHeader,
  EmptyState,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TextField,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import {
  reportsRepository,
  type Range,
} from "@/repositories/reportsRepository";
import { useAuthStore } from "@/stores/authStore";

/**
 * The reports screen.
 *
 * A period at the top and a list of reports below it, rather than a report
 * builder. A shopkeeper wants "last month's sales" and "what did I spend", not a
 * dimension picker — and every report here answers one question that somebody
 * has actually asked out loud.
 *
 * Everything downloads. The export is the same data through the same
 * restriction, so a spreadsheet never carries a column the screen refused to
 * show.
 */
export function ReportsPage() {
  const [range, setRange] = useState<Range>(() => defaultRange());
  const [active, setActive] = useState<ReportKey>("summary");

  const canSeeMargin = useAuthStore((s) => s.can("reports.view_margin"));
  const reports = REPORTS.filter(
    (report) => !report.needsMargin || canSeeMargin,
  );

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <TextField
          label="From"
          type="date"
          value={range.from ?? ""}
          className="w-40"
          onChange={(event) =>
            setRange((current) => ({ ...current, from: event.target.value }))
          }
        />
        <TextField
          label="To"
          type="date"
          value={range.to ?? ""}
          className="w-40"
          onChange={(event) =>
            setRange((current) => ({ ...current, to: event.target.value }))
          }
        />
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <Button
              key={preset.label}
              variant="ghost"
              size="sm"
              onClick={() => setRange(preset.range())}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        {reports.map((report) => (
          <Button
            key={report.key}
            variant={active === report.key ? "primary" : "ghost"}
            size="sm"
            onClick={() => setActive(report.key)}
          >
            {report.label}
          </Button>
        ))}
      </div>

      <ReportPanel
        key={active}
        report={REPORTS.find((r) => r.key === active)!}
        range={range}
      />
    </div>
  );
}

type ReportKey =
  | "summary"
  | "products"
  | "slow-movers"
  | "payment-mix"
  | "by-hour"
  | "tax"
  | "profit-and-loss"
  | "expenses";

interface ReportDefinition {
  key: ReportKey;
  label: string;
  subtitle: string;
  needsMargin?: boolean;
}

const REPORTS: ReportDefinition[] = [
  {
    key: "summary",
    label: "Sales summary",
    subtitle: "Takings, baskets and the average sale",
  },
  { key: "products", label: "Best sellers", subtitle: "Ranked by takings" },
  {
    key: "slow-movers",
    label: "Not selling",
    subtitle: "Stock with no sale in the period",
  },
  {
    key: "payment-mix",
    label: "How people pay",
    subtitle: "Cash, card, transfer and account",
  },
  {
    key: "by-hour",
    label: "Busiest hours",
    subtitle: "Takings by hour of the trading day",
  },
  { key: "tax", label: "VAT", subtitle: "Collected over the period" },
  {
    key: "profit-and-loss",
    label: "Profit and loss",
    subtitle: "Sales, cost of goods, expenses and what is left",
    needsMargin: true,
  },
  {
    key: "expenses",
    label: "Expenses",
    subtitle: "What was spent, by heading",
  },
];

function ReportPanel({
  report,
  range,
}: {
  report: ReportDefinition;
  range: Range;
}) {
  const [downloading, setDownloading] = useState(false);

  const query = useQuery({
    queryKey: ["reports", report.key, range],
    queryFn: () => fetchReport(report.key, range),
  });

  async function download() {
    setDownloading(true);
    try {
      const blob = await reportsRepository.download(report.key, range);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${report.key}-${range.from ?? ""}-to-${range.to ?? ""}.xlsx`;
      link.click();
      // Revoked immediately: the browser has already taken a reference, and an
      // object URL that is never revoked holds the whole file in memory for the
      // life of the tab.
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title={report.label}
        subtitle={report.subtitle}
        action={
          <Button
            variant="ghost"
            size="sm"
            loading={downloading}
            onClick={() => void download()}
          >
            <Download size={14} aria-hidden /> Spreadsheet
          </Button>
        }
      />

      <div className="p-4 pt-0">
        {query.isLoading ? (
          <SkeletonRows rows={6} />
        ) : (
          <ReportBody report={report} data={query.data} />
        )}
      </div>
    </Card>
  );
}

function ReportBody({
  report,
  data,
}: {
  report: ReportDefinition;
  data: unknown;
}) {
  if (data == null) {
    return (
      <EmptyState
        title="Nothing to report"
        description="No trading in this period."
      />
    );
  }

  if (report.key === "summary") {
    const body = data as { current: Record<string, string> };
    return <Figures row={body.current} />;
  }

  if (report.key === "tax") {
    return <Figures row={data as Record<string, string>} />;
  }

  if (report.key === "profit-and-loss") {
    const body = data as {
      net_sales: string;
      cost_of_goods: string;
      gross_profit: string;
      total_expenses: string;
      net_profit: string;
      expenses: { category: string; amount: string }[];
      expenses_are_branch_wide: boolean;
    };

    return (
      <div className="space-y-4 pt-2">
        {/* The standard statement layout: "less" lines carry a plain figure and
            only the result carries a sign. Mixing a manual minus with
            `formatMoney`'s own produced two different dashes on one screen. */}
        <dl className="space-y-1.5 text-sm">
          <Line label="Sales, excluding VAT" value={body.net_sales} />
          <Line label="Less cost of goods" value={body.cost_of_goods} />
          <Line label="Gross profit" value={body.gross_profit} strong />
          <Line label="Less expenses" value={body.total_expenses} />
          <Line
            label="What is left"
            value={body.net_profit}
            strong
            negative={Number(body.net_profit) < 0}
          />
        </dl>

        {body.expenses.length > 0 && (
          <BarChart
            caption="Expenses by heading"
            points={body.expenses.map((row) => ({
              label: row.category,
              value: row.amount,
            }))}
          />
        )}

        {body.expenses_are_branch_wide && (
          <p className="text-xs text-ink-500">
            Sales are for the selected counter; expenses are for the whole shop.
            Rent is paid by the premises, and splitting it across counters would
            be a number somebody invented.
          </p>
        )}
      </div>
    );
  }

  const rows = data as Record<string, string>[];
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Nothing to report"
        description="No trading in this period."
      />
    );
  }

  return <Rows rows={rows} />;
}

function Figures({ row }: { row: Record<string, string> }) {
  const shown = Object.entries(row).filter(
    ([key]) => !["from", "to"].includes(key),
  );

  return (
    <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {shown.map(([key, value]) => (
        <div key={key} className="rounded-control border border-line p-3">
          <dt className="text-xs uppercase tracking-wide text-ink-500">
            {humanise(key)}
          </dt>
          <dd className="mt-0.5 text-lg font-semibold tabular-nums text-ink-900">
            {isMoney(key)
              ? formatMoney(value)
              : key.endsWith("_percent")
                ? `${value}%`
                : value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Rows({ rows }: { rows: Record<string, string>[] }) {
  const columns = Object.keys(rows[0]!).filter((key) => !key.endsWith("_id"));

  return (
    // The `Table` primitive rather than a bare `<table>`, which this was. A
    // report's columns are whatever the server returned, so it cannot mark any
    // of them as droppable — but it can at least scroll inside itself and pick
    // up the same borders, padding and focus behaviour as every other table.
    <Table>
      <THead>
        {columns.map((column) => (
          <TH key={column}>{humanise(column)}</TH>
        ))}
      </THead>
      <TBody>
        {rows.map((row, index) => (
          <TR key={index}>
            {columns.map((column) => (
              <TD
                key={column}
                className={isMoney(column) ? "tabular-nums" : "text-ink-600"}
              >
                {isMoney(column)
                  ? formatMoney(row[column] ?? "0")
                  : String(row[column] ?? "")}
              </TD>
            ))}
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function Line({
  label,
  value,
  strong,
  negative,
}: {
  label: string;
  value: string;
  strong?: boolean;
  /** A shop making a loss should see it without having to read the sign. */
  negative?: boolean;
}) {
  return (
    <div className="flex justify-between border-b border-line pb-1.5">
      <dt className={strong ? "font-medium text-ink-900" : "text-ink-600"}>
        {label}
      </dt>
      <dd
        className={cn(
          "tabular-nums",
          strong && "font-semibold",
          negative ? "text-danger-700" : "text-ink-900",
        )}
      >
        {formatMoney(value)}
      </dd>
    </div>
  );
}

const MONEY_HINTS = [
  "sales",
  "amount",
  "cost",
  "profit",
  "total",
  "cash",
  "variance",
  "value",
  "basket",
];

function isMoney(key: string): boolean {
  return MONEY_HINTS.some((hint) => key.toLowerCase().includes(hint));
}

function humanise(key: string): string {
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

async function fetchReport(key: ReportKey, range: Range) {
  switch (key) {
    case "summary":
      return reportsRepository.summary(range);
    case "products":
      return reportsRepository.products(range);
    case "slow-movers":
      return reportsRepository.slowMovers(range);
    case "payment-mix":
      return reportsRepository.paymentMix(range);
    case "by-hour":
      return reportsRepository.byHour(range);
    case "tax":
      return reportsRepository.tax(range);
    case "profit-and-loss":
      return reportsRepository.profitAndLoss(range);
    case "expenses":
      return reportsRepository.expenses(range);
  }
}

/**
 * A `YYYY-MM-DD` string for a **local** date.
 *
 * Not `toISOString().slice(0, 10)`, which converts to UTC first: in SAST
 * (UTC+2) that turns the first of the month into the last day of the previous
 * one, so "this month" silently began a day early. Found by reading the date
 * boxes on the reports screen.
 */
function iso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function defaultRange(): Range {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: iso(first), to: iso(today) };
}

const PRESETS = [
  {
    label: "This month",
    range: defaultRange,
  },
  {
    label: "Last month",
    range: (): Range => {
      const today = new Date();
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: iso(first), to: iso(last) };
    },
  },
  {
    label: "Last 7 days",
    range: (): Range => {
      const today = new Date();
      const week = new Date(today);
      week.setDate(week.getDate() - 6);
      return { from: iso(week), to: iso(today) };
    },
  },
];
